import { useMutation } from '@apollo/client/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SwitchAccountDocument, SwitchToMasterAccountDocument } from '@/graphql';
import { isMasterAccountBoardPath } from '@/navigation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useOwnBooksLabel } from '@/hooks/use-account-relation-name';
import { useAuth } from '../context';
import { SWITCHED_ACCOUNT_NAME_KEY } from '../constants/switched-account';
import { ACCOUNT_ENTRY_ACKNOWLEDGED_KEY } from '../constants/account-entry';
import { MembershipSource } from '../constants/membership-source';
import {
  forgetMasterAccountBoardUrl,
  rememberMasterAccountBoardUrl
} from '../utils/master-account-board-return';
import { rememberClientSwitchLanding } from '../utils/client-switch-landing';
import { safeRedirect } from '../utils/return-url';
import { translateAuthError } from '../utils';
import { SwitchTargetKind } from '../constants/switch-target-kind';

export { SwitchTargetKind };

/** One entry of the account switcher. */
export interface SwitchTarget {
  id: string;
  name: string;
  kind: SwitchTargetKind;
}

export interface SwitchAccountOptions {
  /** The redirect keeps the person's place (the client bar), not a link they opened. */
  readonly keepsPlace?: boolean;
}

/** The master accounts and organizations the signed-in user can act in, plus a switcher into each. */
export function useSwitchAccount() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError } = useToastHelpers();
  const ownBooksLabel = useOwnBooksLabel();
  const [switchAccountMutation, { loading: switchingAccount }] = useMutation(SwitchAccountDocument);
  const [switchToMasterMutation, { loading: switchingMaster }] = useMutation(
    SwitchToMasterAccountDocument
  );
  // A board session acts in the master account itself, so its id is the master account's.
  const masterAccountBoardId = user?.currentMasterAccount?.id ?? null;

  /** Everything a successful switch does, ending in the hard navigation into the new context. */
  const leaveFor = useCallback(
    (enteredName: string | undefined, redirectTo: string | undefined, keepsPlace = false) => {
      if (enteredName) {
        sessionStorage.setItem(SWITCHED_ACCOUNT_NAME_KEY, enteredName);
      }

      // A deliberate switch is the decision the account-selection screen collects.
      sessionStorage.setItem(ACCOUNT_ENTRY_ACKNOWLEDGED_KEY, 'true');

      // Saves the board URL for "back to the board"; leaving from a non-board path drops an
      // older one. Only the board session writes it, so a client→client switch keeps it.
      if (masterAccountBoardId) {
        if (isMasterAccountBoardPath(window.location.pathname)) {
          rememberMasterAccountBoardUrl(
            masterAccountBoardId,
            window.location.pathname + window.location.search
          );
        } else {
          forgetMasterAccountBoardUrl();
        }
      }

      const target = safeRedirect(redirectTo);
      if (keepsPlace) {
        rememberClientSwitchLanding(target.split(/[?#]/)[0] ?? target);
      }

      // Hard navigation: no in-memory state may cross the tenant boundary. URL state belongs
      // to the context being left, so it lands on the homepage unless `redirectTo` is given.
      window.location.href = target;
    },
    [masterAccountBoardId]
  );

  const switchAccount = useCallback(
    async (accountId: string, redirectTo?: string, options: SwitchAccountOptions = {}) => {
      // errorPolicy 'all' resolves a refused switch, which must not reload as if it worked.
      const result = await switchAccountMutation({ variables: { accountId } });
      if (result.error) {
        // Nothing navigates on failure, so without this the switcher would look inert.
        showError(translateAuthError(result.error, t));
        return;
      }
      const membership = result.data?.switchAccount;
      const entered = membership?.account;
      // A client reached through the office is named next to the logo, so it needs no toast.
      const throughOffice = membership?.source === MembershipSource.MASTER_ACCOUNT;
      leaveFor(
        throughOffice
          ? undefined
          : entered?.isMasterAccountOwnBooks
            ? ownBooksLabel
            : entered?.name,
        redirectTo,
        options.keepsPlace
      );
    },
    [switchAccountMutation, showError, t, leaveFor, ownBooksLabel]
  );

  /**
   * Switches the session into a master account; it lands on the board home unless `redirectTo` says.
   * Resolves false when the switch was refused (the reason is shown), true when leaving.
   */
  const switchToMasterAccount = useCallback(
    async (masterAccountId: string, redirectTo?: string): Promise<boolean> => {
      const result = await switchToMasterMutation({ variables: { masterAccountId } });
      if (result.error) {
        showError(translateAuthError(result.error, t));
        return false;
      }
      // Back from a client to its office's board is a step within the office: no toast.
      const fromClient = user?.source === MembershipSource.MASTER_ACCOUNT;
      leaveFor(fromClient ? undefined : result.data?.switchToMasterAccount.name, redirectTo);
      return true;
    },
    [switchToMasterMutation, showError, t, leaveFor, user?.source]
  );

  /** Master accounts, then direct organizations; a master account's clients are reached only from its board. */
  const targets = useMemo<SwitchTarget[]>(
    () => [
      ...(user?.masterAccounts ?? []).map(master => ({
        id: master.id,
        name: master.name,
        kind: SwitchTargetKind.MASTER_ACCOUNT
      })),
      ...(user?.memberships ?? [])
        .filter(m => m.source === MembershipSource.DIRECT)
        .map(m => ({ id: m.account.id, name: m.account.name, kind: SwitchTargetKind.ACCOUNT }))
    ],
    [user?.masterAccounts, user?.memberships]
  );

  /** Switches into a switcher entry, whichever kind it is. */
  const switchTo = useCallback(
    async (targetId: string, redirectTo?: string): Promise<void> => {
      const target = targets.find(candidate => candidate.id === targetId);
      if (target?.kind === SwitchTargetKind.MASTER_ACCOUNT) {
        await switchToMasterAccount(targetId, redirectTo);
      } else {
        await switchAccount(targetId, redirectTo);
      }
    },
    [targets, switchAccount, switchToMasterAccount]
  );

  return {
    /** Every switcher entry: master accounts, then direct organizations. */
    targets,
    /** The account the session acts in; null on the board. */
    activeAccountId: user?.account?.id ?? null,
    /** The switcher entry the session is in — the master account on the board, else the account. */
    activeTargetId: masterAccountBoardId ?? user?.account?.id ?? null,
    switchAccount,
    switchToMasterAccount,
    switchTo,
    switching: switchingAccount || switchingMaster
  };
}
